import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

// ── Types matching w-ocr /api/v1/ocr responses ──

export interface OcrExtractedMetric {
  ID?: number;
  ocr_job_id?: number;
  metric_name: string;
  metric_code: string;
  raw_value: string;
  numeric_value: number;
  unit: string;
  raw_reference_range: string;
  reference_min: number;
  reference_max: number;
  status: string;
  health_category: string;
  confidence: number;
  mapping_status: string;
  sort_order: number;
}

export interface OcrPrescription {
  ID?: number;
  ocr_job_id?: number;
  medication_name: string;
  generic_name: string;
  dosage: string;
  frequency: string;
  duration: string;
  route: string;
  instructions: string;
  confidence: number;
  sort_order: number;
}

export interface OcrJob {
  id: number;
  customer_id: number;
  status: string;                  // pending | processing | completed | failed | review_needed
  document_type: string;           // lab_report | prescription | discharge_summary | unknown
  document_subtype: string;        // CBC, Lipid, etc.
  original_file_url: string;
  original_file_name: string;
  file_mime_type: string;
  file_size_bytes: number;
  source: string;                  // upload | camera
  overall_confidence: number;
  needs_manual_review: boolean;
  error_message: string;
  report_id: number;
  report_date: string | null;
  sample_date: string | null;
  lab_name: string;
  doctor_name: string;
  reviewed_by: number;
  reviewed_at: string | null;
  processing_started_at: string | null;
  processing_completed_at: string | null;
  processing_duration_ms: number;
  created_at: string;
  updated_at: string;
  metrics?: OcrExtractedMetric[];
  prescriptions?: OcrPrescription[];
}

export interface OcrStats {
  total_jobs: number;
  by_status: Record<string, number>;
  by_document_type: Record<string, number>;
  avg_confidence: number;
  avg_processing_ms: number;
}

interface OcrState {
  jobs: OcrJob[];
  total: number;
  selectedJob: OcrJob | null;
  stats: OcrStats | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

const initialState: OcrState = {
  jobs: [],
  total: 0,
  selectedJob: null,
  stats: null,
  loading: false,
  actionLoading: false,
  error: null,
};

// ── Thunks ──

export const fetchOcrJobs = createAsyncThunk(
  'ocr/fetchJobs',
  async (
    params: { status?: string; page?: number; limit?: number } | undefined,
    thunkAPI
  ) => {
    try {
      const search = new URLSearchParams();
      if (params?.status) search.set('status', params.status);
      search.set('page', String(params?.page ?? 1));
      search.set('limit', String(params?.limit ?? 50));
      const response = await apiClient.get(
        `${config.services.ocr}/api/v1/ocr/admin/jobs?${search.toString()}`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch OCR jobs');
    }
  }
);

export const fetchOcrJobById = createAsyncThunk(
  'ocr/fetchJobById',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(
        `${config.services.ocr}/api/v1/ocr/jobs/${id}`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch OCR job');
    }
  }
);

export const approveOcrJob = createAsyncThunk(
  'ocr/approveJob',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.post(
        `${config.services.ocr}/api/v1/ocr/jobs/${id}/approve`
      );
      return { id, data: response.data };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to approve OCR job');
    }
  }
);

export const rejectOcrJob = createAsyncThunk(
  'ocr/rejectJob',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.post(
        `${config.services.ocr}/api/v1/ocr/jobs/${id}/reject`
      );
      return { id, data: response.data };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to reject OCR job');
    }
  }
);

export const deleteOcrJob = createAsyncThunk(
  'ocr/deleteJob',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.ocr}/api/v1/ocr/jobs/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete OCR job');
    }
  }
);

export const fetchOcrStats = createAsyncThunk(
  'ocr/fetchStats',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.ocr}/api/v1/ocr/stats`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch OCR stats');
    }
  }
);

/**
 * Downloads the original stored PDF for a job and returns a Blob.
 * Consumers should trigger a browser download using URL.createObjectURL.
 */
export const downloadOcrJobFile = createAsyncThunk(
  'ocr/downloadFile',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(
        `${config.services.ocr}/api/v1/ocr/jobs/${id}/download`,
        { responseType: 'blob' }
      );
      return response.data as Blob;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to download file');
    }
  }
);

const ocrSlice = createSlice({
  name: 'ocr',
  initialState,
  reducers: {
    clearSelectedOcrJob: (state) => { state.selectedJob = null; },
    clearOcrError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOcrJobs.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchOcrJobs.fulfilled, (state, action) => {
        state.loading = false;
        const payload: any = action.payload;
        state.jobs = payload?.data?.jobs ?? [];
        state.total = payload?.meta?.total ?? state.jobs.length;
      })
      .addCase(fetchOcrJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchOcrJobById.fulfilled, (state, action) => {
        const payload: any = action.payload;
        state.selectedJob = payload?.data || payload || null;
      })
      .addCase(approveOcrJob.pending, (state) => { state.actionLoading = true; })
      .addCase(approveOcrJob.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Update the local job status optimistically
        const idx = state.jobs.findIndex(j => j.id === action.payload.id);
        if (idx !== -1) {
          state.jobs[idx] = { ...state.jobs[idx], needs_manual_review: false };
        }
      })
      .addCase(approveOcrJob.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(rejectOcrJob.pending, (state) => { state.actionLoading = true; })
      .addCase(rejectOcrJob.fulfilled, (state, action) => {
        state.actionLoading = false;
        const idx = state.jobs.findIndex(j => j.id === action.payload.id);
        if (idx !== -1) {
          state.jobs[idx] = { ...state.jobs[idx], status: 'review_needed' };
        }
      })
      .addCase(rejectOcrJob.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteOcrJob.fulfilled, (state, action) => {
        state.jobs = state.jobs.filter(j => j.id !== action.payload);
      })
      .addCase(fetchOcrStats.fulfilled, (state, action) => {
        const payload: any = action.payload;
        state.stats = payload?.data || payload || null;
      });
  },
});

export const { clearSelectedOcrJob, clearOcrError } = ocrSlice.actions;
export default ocrSlice.reducer;
